// stores/reservation-store.js
import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';

const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

function nowAndTomorrow() {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    let hh = now.getHours();
    let mm = Math.round(now.getMinutes() / 30) * 30;

    if (mm === 60) {
        mm = 0;
        hh += 1;
        if (hh === 24) {
            hh = 0;
        }
    }

    const roundedTime = `${pad(hh)}:${pad(mm)}`;

    return {
        today: fmtDate(now),
        nowHHmm: roundedTime,
        tomorrow: fmtDate(tomorrow)
    };
}

function baseState() {
    const t = nowAndTomorrow();
    return {
        vehicle_no: '',
        vehicle_group_id: 0,
        pickup_date: t.today,
        pickup_time: t.nowHHmm,
        return_expected_date: t.tomorrow,
        return_expected_time: t.nowHHmm,
        pickup_delivery_id: 0,
        return_expected_delivery_id: 0,
        collision_insurance_state: 'NON',
        collision_insurance_age: '',
        contract_month: 1,
        payment_day: 1,
        payment_first_date: '',
        month_amount: 0,
        advance_payment: 0
    };
}

export const useReservationsStore = defineStore('reservation-sessions', () => {
    const sessions = reactive({});
    const baseKeys = Object.keys(baseState());

    const ensure = (key) => {
        if (!key) throw new Error('sessionKey가 필요합니다.');
        if (!sessions[key]) sessions[key] = reactive({ ...baseState() });
        return sessions[key];
    };

    const init = (key, seed = {}) => {
        const s = ensure(key);
        Object.keys(seed).forEach((k) => {
            if (baseKeys.includes(k)) s[k] = seed[k];
        });
        return s;
    };

    const update = (key, patch) => {
        const s = ensure(key);
        Object.keys(patch).forEach((k) => {
            if (baseKeys.includes(k)) s[k] = patch[k];
        });
        return s;
    };

    const reset = (key) => Object.assign(ensure(key), baseState());
    const remove = (key) => {
        if (sessions[key]) delete sessions[key];
    };

    const payloadRef = (key) =>
        computed(() => {
            const s = ensure(key);
            return {
                ...s
                // pickup_date_time: s.pickup_date ? `${s.pickup_date}T${s.pickup_time}:00` : '',
                // return_expected_date_time: s.return_expected_date ? `${s.return_expected_date}T${s.return_expected_time}:00` : ''
            };
        });

    const rentalDurationRef = (key) =>
        computed(() => {
            const s = ensure(key);

            const normalize = (dateStr, timeStr) => {
                if (!dateStr) return null;
                let normDate = dateStr;
                if (/^\d{8}$/.test(dateStr)) {
                    // 20250922 → 2025-09-22 변환
                    normDate = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
                }
                return new Date(`${normDate}T${timeStr || '00:00'}:00`);
            };

            const start = normalize(s.pickup_date, s.pickup_time);
            const end = normalize(s.return_expected_date, s.return_expected_time);
            if (isNaN(start) || isNaN(end)) return '대여일, 반납일을 선택하세요.';
            const diff = end - start;
            if (diff < 0) return '0일 0시간 0분';
            const days = Math.floor(diff / 86400000);
            const hours = Math.floor((diff % 86400000) / 3600000);
            const minutes = Math.floor((diff % 3600000) / 60000);
            return `${days}일 ${hours}시간 ${minutes}분`;
        });

    return { sessions, ensure, init, update, reset, remove, payloadRef, rentalDurationRef };
});
